import { alphaMutationRejected } from "../alpha-mutation";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { api, authenticatedFetch } from "../api";
import { ErrorState, Loading } from "../ui/State";
async function download(url: string, name: string) {
  const r = await authenticatedFetch(url);
  if (!r.ok) throw Error("Document unavailable or access withdrawn.");
  const u = URL.createObjectURL(await r.blob()),
    a = document.createElement("a");
  a.href = u;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(u), 1000);
}
export function InformationResponsesAlpha({ premises }: { premises: string }) {
  const [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    root = "/api/portal/leaseholder/" + premises + "/information";
  useEffect(() => {
    void api(root).then(setModel).catch(setError);
  }, [root]);
  return (
    <section className="panel">
      <h2>Information supplied to you</h2>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {model?.packages.length === 0 && (
        <p>
          No responses published yet. Use “Information request” below to request
          building records.
        </p>
      )}
      {model?.packages.map((p: any) => (
        <article key={p.id}>
          <p>{p.summary}</p>
          {p.manifest.documents.map((d: any) => (
            <p key={d.versionId}>
              <button
                onClick={() =>
                  void download(
                    root + "/" + p.id + "/" + d.versionId,
                    d.title,
                  ).catch(setError)
                }
              >
                {d.title}
              </button>
            </p>
          ))}
        </article>
      ))}
    </section>
  );
}
export function InformationAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building"),
    [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    pending = useRef<{ url: string; body: any } | null>(null);
  const load = () =>
    building
      ? api("/api/alpha/information?building=" + building).then(setModel)
      : Promise.resolve();
  useEffect(() => {
    setModel(undefined);
    void load().catch(setError);
  }, [building]);
  async function save(url: string, body: any) {
    pending.current ??= {
      url,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    setBusy(true);
    try {
      await api(pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      setError(undefined);
      await load();
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Information requests and disclosure</h1>
      <p>
        Prepare responses covering accounts, maintenance, governance or other
        building records. Publish only reviewed material suitable for the
        recipient.
      </p>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void save("", {})}>Retry the same save</button>
      )}
      {!building ? (
        <p>Choose a building.</p>
      ) : !model ? (
        <Loading />
      ) : (
        <>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void save(
                "/api/alpha/information/" + f.get("case") + "/prepare",
                {
                  kind: f.get("kind"),
                  summary: f.get("summary"),
                  documentIds: f.getAll("documents"),
                },
              );
            }}
          >
            <fieldset disabled={busy || !!pending.current}>
              <legend>Prepare response</legend>
              <label>
                Request case
                <select name="case" required>
                  <option value="">Choose case</option>
                  {model.cases.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Purpose
                <select name="kind">
                  <option>Information request</option>
                  <option>Outgoing handover</option>
                </select>
              </label>
              <label>
                Response summary
                <textarea
                  name="summary"
                  required
                  minLength={10}
                  maxLength={10000}
                />
              </label>
              <p>
                Choose documents. Sensitive originals must first be replaced
                with a reviewed shareable or redacted copy in Documents.
              </p>
              {model.documents.map((d: any) => (
                <label key={d.id}>
                  <input type="checkbox" name="documents" value={d.id} />
                  {d.title}
                </label>
              ))}
              <button>Prepare response</button>
            </fieldset>
          </form>
          <h2>Prepared responses</h2>
          {!model.packages.length&&<p className="empty">No information responses have been prepared for this building.</p>}
          {model.packages.map((p: any) => (
            <section className="panel" key={p.id}>
              <h3>
                {p.kind} ·{" "}
                {p.withdrawn_at
                  ? "Withdrawn"
                  : p.approved_at
                    ? "Approved"
                    : "Awaiting review"}
              </h3>
              <p>{p.summary}</p>
              {!p.withdrawn_at && (
                <>
                  {!p.approved_at ? (
                    <button
                      disabled={busy || !!pending.current}
                      onClick={() =>
                        void save(
                          "/api/alpha/information/packages/" +
                            p.id +
                            "/approve",
                          {},
                        )
                      }
                    >
                      Approve and share with requester
                    </button>
                  ) : (
                    <button
                      onClick={() =>
                        void download(
                          "/api/alpha/information/packages/" +
                            p.id +
                            "/download",
                          "disclosure-manifest.html",
                        ).catch(setError)
                      }
                    >
                      Download reviewed manifest
                    </button>
                  )}
                  <button
                    disabled={busy || !!pending.current}
                    onClick={() =>
                      void save(
                        "/api/alpha/information/packages/" + p.id + "/withdraw",
                        {},
                      )
                    }
                  >
                    Withdraw access
                  </button>
                </>
              )}
            </section>
          ))}
        </>
      )}
    </>
  );
}
